import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { ReactComponent as CloseIcon } from '@/assets/icons/arrow-down.svg';
import { ReactComponent as StarIcon } from '@/assets/icons/star-shine.svg';

export const Version = styled.span`
  min-width: 100px;
  max-width: 130px;
  padding: 4px 0;
  line-height: 10px;
  font-size: 9px;
  text-align: center;

  &:first-of-type {
    width: 70px;
    min-width: auto;
    text-align: left;
  }

  span {
    color: #007bff;
    cursor: pointer;
  }
`;

export const PoweredBy = styled.div`
  margin-top: 10px;
  width: 100%;
  font-size: 10px;
  text-align: center;
  text-transform: lowercase;

  a {
    color: #000;
    text-decoration: none;
  }

  b {
    font-weight: bold;
  }

  ${({ theme }) => css`
    @media (${theme.mediaMobile}) {
      ${theme.poweredBy.mobile};
    }

    @media not (${theme.mediaMobile}) {
      ${theme.poweredBy.desktop};
    }
  `}

  ${({ theme }) => theme.poweredBy.styles};
`;

export const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex: auto;
  align-items: flex-end;
  justify-content: center;
  box-sizing: border-box;
  gap: 10px;
`;

export const Footer = styled.div<{ hasChatFeedback: boolean }>`
  display: flex;
  flex-direction: column;
  max-height: 300px; /* need for feedback popup when have some lines in Input */
  padding: 15px 15px 10px;
  border-top: 1px solid #6a6a6a;
  background: #fff;
  transition: height 1s ease;

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 0;
    background: inherit;
    transition: height 0.2s ease;
    will-change: height;
  }

  ${({ theme, hasChatFeedback }) => css`
    @media (${theme.mediaMobile}) {
      ${theme.footer.mobile};
    }

    @media not (${theme.mediaMobile}) {
      ${hasChatFeedback &&
      css`
        &::before {
          height: 300px;
        }
      `}

      ${theme.footer.desktop};
    }
  `}

  ${({ theme }) => theme.footer.styles};
`;

export const IconContainer = styled.div`
  position: relative;
  margin-right: 11px;
  width: 22px;
  height: 26px;

  svg {
    &:first-child {
      position: absolute;
      top: 0;
      right: 0;
    }

    &:last-child {
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
`;

export const Star = styled(StarIcon)<{ size: number }>`
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
`;

export const CloseButton = styled(CloseIcon)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const CloseButtonContainer = styled.button`
  position: absolute;
  top: 24px;
  right: 14px;
  width: 13px;
  height: 13px;
  padding: 0;
  border-radius: 6px;
  transition: all 0.3s ease;
  will-change: opacity;

  &:hover {
    opacity: 0.6;
  }

  &:focus {
    box-shadow:
      0 0 0 2px #fff,
      0 0 0 4px #222;
  }

  ${({ theme }) => theme.header.closeButton};
`;

export const Header = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 20px 16px 10px;
  font-weight: 600;

  ${({ theme }) => css`
    @media (${theme.mediaMobile}) {
      min-height: 52px;
      font-size: 15px;
      ${theme.header.mobile};
    }

    @media not (${theme.mediaMobile}) {
      font-size: 13px;
      ${theme.header.desktop};
    }
  `};

  ${({ theme }) => theme.header.styles};
`;

type ContentWrapperProps = {
  isOpen: boolean;
  isFirstRender: boolean;
  isOpenKeyboard: boolean;
  isEmbedded?: boolean;
  isShopifyPreview?: boolean;
  isMlAnswered?: boolean;
};

export const Wrapper = styled.div<ContentWrapperProps>`
  position: ${({ isEmbedded }) => (!isEmbedded ? 'fixed' : 'static')};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px;
  background: ${({ theme }) => `linear-gradient(285.3deg, #FFF 14.7%, ${theme.colors.light});`};
  box-shadow: 0 0 15px 0 #00000026;
  z-index: ${({ theme }) => theme.zIndexApp};
  animation-duration: ${({ isFirstRender }) => (isFirstRender ? '0s' : '0.15s')};
  animation-timing-function: ease;
  animation-fill-mode: forwards;

  ${({ theme }) => theme.container.styles};

  ${({ theme, isOpen, isEmbedded, isShopifyPreview, isMlAnswered }) => css`
    @media (${theme.mediaMobile}) {
      bottom: ${isShopifyPreview ? '64px' : '0'};
      left: 0;
      width: 100%;
      height: 70dvh;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      animation-name: ${isOpen ? theme.animation.container.mobile.open : theme.animation.container.mobile.close};
      animation-duration: 0.2s;
      will-change: height, transform;
      ${theme.container.mobile};
    }

    @media not (${theme.mediaMobile}) {
      right: 50px;
      bottom: 50px;
      width: ${isEmbedded ? '100%' : '393px'};
      height: ${isEmbedded ? '100%' : isMlAnswered ? '700px' : '470px'};
      max-height: calc(100dvh - 100px);
      transition: height 0.2s ease-in-out;
      animation-name: ${isOpen ? theme.animation.container.desktop.open : theme.animation.container.desktop.close};
      ${theme.container.desktop};
    }
  `};
`;

export const PromtsContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;

  ${({ theme }) => css`
    ${theme.prompts.styles}

    @media (${theme.mediaMobile}) {
      ${theme.prompts.mobile}
    }

    @media not (${theme.mediaMobile}) {
      ${theme.prompts.desktop}
    }
  `}
`;
